<template>
  <div
    v-if="openList"
    class="dark:bg-background absolute left-0 top-[calc(100%+8px)] z-[21] w-full rounded-2xl border-[#878787] bg-white p-4 text-base/[17.6px] font-bold dark:border-[#3D4043] md:w-[calc(100%+200px)] md:text-lg/[23.8px]"
    id="autocomplete-list"
  >
    <component
      v-for="(item, index) in suggestions"
      :key="index"
      :is="componentsOrder[index]"
      @click="emits('selectSuggestion', item)"
      @mouseenter="
        () => {
          mouseEnter(index)
          adjustScroll()
        }
      "
      class="relative flex cursor-pointer overflow-x-auto rounded-full bg-transparent p-5 text-[#878787] duration-300"
      :class="[
        lastChoisePlace.place_id === item.place_id && 'pointer-events-none',
        item.selected &&
          'autocomplete_item-selected bg-[#CCF2C8] text-black dark:bg-[#333639] dark:text-white',
        lastChoisePlace[props.fieldType].place_id === item.place_id
          ? 'border-main border'
          : 'border-none'
      ]"
    >
      <component
        :is="parsePlaceType(item)"
        :selected="item.selected"
        :place="item"
        class="group truncate py-1"
      >
      </component>
    </component>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeMount, onBeforeUnmount, computed, nextTick } from 'vue'
import collect from 'collect.js'
import { storeToRefs } from 'pinia'
import { useTrustyStore } from '@/stores/data/trustyComplete'
import FirstComponent from '@/components/ui/autocomplete/order/FirstComponent.vue'
import SecondComponent from '@/components/ui/autocomplete/order/SecondComponent.vue'
import ThirdComponent from '@/components/ui/autocomplete/order/ThirdComponent.vue'
import FourthComponent from '@/components/ui/autocomplete/order/FourthComponent.vue'
import FifthComponent from '@/components/ui/autocomplete/order/FifthComponent.vue'
import AirportOption from '@/components/ui/autocomplete/AirportOption.vue'
import HotelOption from '@/components/ui/autocomplete/HotelOption.vue'
import PlaceOption from '@/components/ui/autocomplete/PlaceOption.vue'
import TrainOption from '@/components/ui/autocomplete/TrainOption.vue'

const trustyStore = useTrustyStore()
const { lastChoisePlace } = storeToRefs(trustyStore)

const props = defineProps(['suggestions', 'showSuggestions', 'fieldType'])
const emits = defineEmits(['selectSuggestion'])

const fieldType = ref(props.fieldType)
const suggestions = ref(props.suggestions)
const showSuggestions = ref(props.showSuggestions)

const openList = computed(
  () => showSuggestions.value[fieldType.value] && suggestions.value.length > 0
)

const componentsOrder = [
  FirstComponent,
  SecondComponent,
  ThirdComponent,
  FourthComponent,
  FifthComponent
]

const orderType = collect({
  airport: AirportOption,
  other: PlaceOption,
  lodging: HotelOption,
  train_station: TrainOption,
  transit_station: TrainOption
})

const parsePlaceType = (item) => {
  let types = collect(item.types).intersect(orderType.keys())
  return orderType.get(types.first() ?? 'other')
}

watch(
  () => [props.suggestions, props.showSuggestions],
  (value) => {
    suggestions.value = value[0]
    showSuggestions.value = value[1]

    if (suggestions.value.length) {
      suggestions.value[0].selected = true
    }
  }
)

watch(openList, () => {
  adjustScroll()
})

function adjustScroll() {
  nextTick(() => {
    const selectedDom = document.querySelector(
      '.autocomplete_item-selected #autocomplete-place-text'
    )
    if (selectedDom) {
      const selectedContainerDom = document.querySelector('.autocomplete_item-selected')

      if (selectedDom.offsetWidth + 50 > selectedContainerDom.offsetWidth) {
        const distance = selectedDom.offsetWidth - selectedContainerDom.offsetWidth // Расстояние для прокрутки
        let animDuration = distance / 50 < 2.5 ? 2.5 : distance / 50
        selectedDom.style.animation = `scrollText ${animDuration}s linear infinite` //Скорость анимации меняется в зависимости от длины текста

        const scrollText = `@keyframes scrollText {
          0% { transform: translateX(0); }
          10% { transform: translateX(0); }
          90% { transform: translateX(-${distance + 50}px); }
          100% { transform: translateX(-${distance + 50}px); }
        }`

        // // Добавляем keyframes в <head> документа
        const styleSheet = document.createElement('style')
        styleSheet.classList.add('scrollTextStyle')
        styleSheet.type = 'text/css'
        styleSheet.innerText = scrollText
        document.head.appendChild(styleSheet)
      } else {
        selectedDom.style.transform = 'translateX(0)'
      }
    }
  })
}

const mouseEnter = (index) => {
  clearSelected()
  suggestions.value[index].selected = true
}

const clearSelected = () => {
  suggestions.value.forEach((item) => {
    item.selected = false
  })
  if (document.querySelector('.scrollTextStyle'))
    document.querySelector('.scrollTextStyle').remove()
  // clear styles for tag 'p'
  document.querySelectorAll('#autocomplete-place-text').forEach((element) => {
    element.style.animation = ''
  })
}

const handlePressButtons = (event) => {
  if (openList.value) {
    const arrowDown = 40
    const arrowUp = 38
    const enterButton = 13
    if (event.keyCode == arrowDown || event.keyCode == arrowUp || event.keyCode == enterButton) {
      event.preventDefault()

      // Logic press Arrays Start
      const suggestionCount = suggestions.value.length
      let index = suggestions.value.findIndex((x) => x.selected == true)

      // if press ArrowDown
      if (event.keyCode == arrowDown || event.keyCode == arrowUp) {
        clearSelected()
        if (event.keyCode == arrowDown) {
          index < suggestionCount - 1
            ? (suggestions.value[index + 1].selected = true)
            : (suggestions.value[0].selected = true)
        }
        if (event.keyCode == arrowUp) {
          index != 0
            ? (suggestions.value[index - 1].selected = true)
            : (suggestions.value[suggestionCount - 1].selected = true)
        }
        adjustScroll()
      }

      // Logic press Arrays End

      // Logic press Enter button
      if (event.keyCode == enterButton) {
        const item = suggestions.value.find((el) => el.selected == true)
        emits('selectSuggestion', item)
      }
    }
  }
}

onBeforeMount(() => {
  document.addEventListener('keydown', handlePressButtons)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handlePressButtons)
})
</script>
